import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, useOsResource } from "./osForm";
import { EXPENSE_CATEGORIES, EXPENSE_METHODS, EXPENSE_STATUSES, pageMeta } from "./osRules";

const blank = { category: "payroll", vendor: "", amount: "", date: "", method: "Bank Transfer", approver: "", status: "Processing", note: "" };

export default function Expenses() {
  const [filters, setFilters] = useState({ category: "", status: "", search: "", startDate: "", endDate: "" });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [saving, setSaving] = useState(false);
  const list = useOsResource(
    (auth) => os.expenses(auth, { ...applied, page, limit }),
    [applied, page, limit]
  );
  const pack = pageMeta(list.data);

  const save = async () => {
    if (!draft.vendor || !draft.date || !(Number(draft.amount) > 0)) {
      notification.error({ message: "Payee, date, and a positive amount are required." });
      return;
    }
    setSaving(true);
    try {
      await os.createExpense(list.token, { ...draft, amount: Number(draft.amount) });
      notification.success({ message: "Expense recorded." });
      setOpen(false);
      setDraft(blank);
      list.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fo-stack">
      <div className="fo-tools">
        <SelectField label="Category" value={filters.category} onChange={(category) => setFilters({ ...filters, category })} options={EXPENSE_CATEGORIES} allowEmpty />
        <SelectField label="Status" value={filters.status} onChange={(status) => setFilters({ ...filters, status })} options={EXPENSE_STATUSES} allowEmpty />
        <TextField label="From" type="date" value={filters.startDate} onChange={(startDate) => setFilters({ ...filters, startDate })} />
        <TextField label="To" type="date" value={filters.endDate} onChange={(endDate) => setFilters({ ...filters, endDate })} />
        <TextField label="Search" value={filters.search} onChange={(search) => setFilters({ ...filters, search })} />
        <button type="button" className="fo-btn" onClick={() => { setPage(1); setApplied(filters); }}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => setOpen(true)}>Add expense</button>
      </div>
      <FoCard title="Company expenses" note="These are Chainz expenses. They are separate from brand fees.">
        <PagerTable
          loading={list.loading}
          dataSource={pack.docs}
          rowKey="_id"
          page={page}
          limit={limit}
          total={pack.total}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No expenses in this view."
          columns={[
            { title: "Date", dataIndex: "date" },
            { title: "Category", dataIndex: "category", render: (value) => statusBadge(value) },
            { title: "Payee", dataIndex: "payee", render: (payee, row) => payee || row.vendor },
            { title: "Amount", dataIndex: "amount", render: (value) => `${formatEgp(value)} EGP` },
            { title: "Method", dataIndex: "method" },
            { title: "Approver", dataIndex: "approver" },
            { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            { title: "Note", dataIndex: "note" },
          ]}
        />
      </FoCard>
      <FoDialog open={open} title="Add expense" onClose={() => !saving && setOpen(false)}>
        <div className="fo-form-grid">
          <SelectField label="Category" value={draft.category} onChange={(category) => setDraft({ ...draft, category })} options={EXPENSE_CATEGORIES} />
          <TextField label="Payee" value={draft.vendor} onChange={(vendor) => setDraft({ ...draft, vendor })} hint="Name of the payee, not a brand." />
          <TextField label="Amount" type="number" value={draft.amount} onChange={(amount) => setDraft({ ...draft, amount })} />
          <TextField label="Date" type="date" value={draft.date} onChange={(date) => setDraft({ ...draft, date })} />
          <SelectField label="Method" value={draft.method} onChange={(method) => setDraft({ ...draft, method })} options={EXPENSE_METHODS} />
          <TextField label="Approver" value={draft.approver} onChange={(approver) => setDraft({ ...draft, approver })} />
          <SelectField label="Status" value={draft.status} onChange={(status) => setDraft({ ...draft, status })} options={EXPENSE_STATUSES} />
          <TextField label="Note" value={draft.note} onChange={(note) => setDraft({ ...draft, note })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={saving} onClick={save}>Save</button>
        </div>
      </FoDialog>
    </div>
  );
}
